import { Component, Input, OnChanges, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CallState } from '@lcs/shared/api';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getCurrentUserIsSystemUser } from 'src/app/authentication/state/authentication.selector';
import { TwoColumnLayoutService } from 'src/app/common-components/column-layouts/two-column-list-layout/services/selected-id.service';
import { Detail } from 'src/app/common-components/detail/detail';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import { getSchedulePrivileges } from 'src/app/privileges/privileges.selector';
import { SchedulePrivileges } from '../../schedule-register/models/schedule-privileges';
import { ScheduleDetailSaveEvent } from '../models/schedule-detail-form-submit-event';
import { ScheduleDetailView } from '../models/schedule-detail-view.model';
import { ScheduleDetailFormService } from '../services/schedule-detail-form.service';
import * as ScheduleDetailActions from '../state/schedule-detail.actions';
import { getSchedule, getScheduleCallState } from '../state/schedule-detail.selectors';

@Component({
    encapsulation: ViewEncapsulation.None,
    host: { class: 'entity-detail' },
    providers: [ScheduleDetailFormService, { provide: FormsBase, useExisting: ScheduleDetailFormService }],
    selector: 'ndt-schedule-detail',
    templateUrl: './schedule-detail.component.html',
    standalone: false
})
export class ScheduleDetailComponent extends Detail<ScheduleDetailFormService> implements OnInit, OnDestroy, OnChanges {
   @Input() scheduleId: number;

   schedule$: Observable<ScheduleDetailView | null>;

   callState$: Observable<CallState>;

   schedulePrivileges$: Observable<SchedulePrivileges>;

   selectedScheduleId: number;

   constructor(
      public store: Store,
      private router: Router,
      private twoColumnLayoutService: TwoColumnLayoutService,
      protected formService: ScheduleDetailFormService
   ) {
      super(store, formService);
      this.schedule$ = this.store.pipe(select(getSchedule));
      this.callState$ = this.store.pipe(select(getScheduleCallState));
      this.schedulePrivileges$ = this.store.pipe(select(getSchedulePrivileges));
      this.isSystemUser$ = this.store.pipe(select(getCurrentUserIsSystemUser));
      this.store.dispatch(ScheduleDetailActions.scheduleDetailLoad());
   }

   ngOnInit(): void {
      this.router.events.pipe(takeUntil(this.destroyed$)).subscribe((e) => {
         if (e instanceof NavigationEnd) {
            this.store.dispatch(ScheduleDetailActions.scheduleDetailLoad());
         }
      });
      // Subscribe to selectedId$ to get selectedId from TwoColumnLayoutService
      this.twoColumnLayoutService.selectedId$.pipe(takeUntil(this.destroyed$)).subscribe((id) => {
         if (id !== null) {
            // Set selectedScheduleId to id from TwoColumnLayoutService
            this.selectedScheduleId = id;
            // Dispatch updateScheduleId action to update currentScheduleId in state
            this.store.dispatch(ScheduleDetailActions.updateScheduleId({ scheduleId: id }));
         }
      });
   }

   ngOnChanges(): void {
      this.store.dispatch(ScheduleDetailActions.scheduleDetailLoad());
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onFormSaveCanceled(): void {
      this.store.dispatch(ScheduleDetailActions.scheduleDetailCancel());
   }

   onFormSubmitted(event: ScheduleDetailSaveEvent): void {
      this.store.dispatch(ScheduleDetailActions.saveScheduleDetail(event));
   }

   onDeleteSchedule(event: { scheduleId: number; scheduleDetailId: number; name: string | null }) {
      this.store.dispatch(ScheduleDetailActions.deleteSchedule(event));
   }

   onAddScheduleHour(): void {
      this.store.dispatch(ScheduleDetailActions.addScheduleHour());
   }

   onAddClosedException(): void {
      this.store.dispatch(ScheduleDetailActions.addClosedException({ scheduleId: this.selectedScheduleId }));
   }
}
